import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { DateField } from 'components/Field/DateField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { TimescaleField } from 'components/Field/TimescaleField';
import { ICONS } from 'components/Icon';
import {
  ModalScreenProps,
  ModalScreens,
  ScreenProps,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { addYears, format } from 'date-fns';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { API_DATE_FORMAT } from 'lib/constants';
import { limitDateToRange } from 'lib/limitDateToRange';
import { animateNext } from 'lib/animateNext';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  DELETE_ASSESSMENT,
  doAseLoadPdf,
  doAseSaveSummary,
} from 'store/assessments/actions';
import {
  selectAseAssetId,
  selectAseComplianceSubtype,
  selectAseComplianceType,
  selectAseStatus,
  selectAseTemplate,
  selectAseIsOnline,
  selectAseAssetAssessmentTimestamps,
  selectAseTask,
  selectAseAssetRiskRatings,
  selectAseReference,
  selectAseDetails,
  selectAseAllowedAuthors,
  selectAseAllowedAuthorOrganisations,
  selectAseId,
  selectAseFilesAst,
} from 'store/assessments/selectors';
import { Details } from 'store/assessments/types';
import { FilesContextEnum, TimescaleArray, TimescalePeriod } from 'types/types';
import { theme } from 'lib/theme';
import {
  eventAseUpdateAuthor,
  eventAseUpdateRiskRating,
  eventSelectScreenSelection,
  eventReorderScreenDone,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  numberToDate,
  rbAddReferencePrefix,
  dateToNumber,
  randomKey,
} from 'lib/riskbase';
import { str } from 'lib/str';
import { cloneDeep, isEqual } from 'lodash';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useEsc } from 'hooks/useEsc';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { useAlert } from 'hooks/useAlert';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import { Txt } from 'components/Txt';
import { FileField } from 'componentsAware/FileField';
import { PageHeader } from 'components/PageHeader';

export const SummaryEdit: React.FC<
  | ScreenProps<Stacks.ASSESSMENT, Screens.ASE_SUMMARY>
  | ModalScreenProps<ModalScreens.AseSummary>
> = ({ navigation, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const forceUpdate = useForceUpdate();
  const alert = useAlert();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const eventKeyAseUpdateRiskRating = React.useRef(randomKey());
  const eventKeyAseUpdateAuthor = React.useRef(randomKey());
  const eventKeySelectAssessorRef = React.useRef(randomKey());
  const eventKeySelectAuthorOrganisationRef = React.useRef(randomKey());

  const [currentlyEditing, setActualCurrentlyEditing] = React.useState<
    undefined | 'startdate' | 'expirydate'
  >();
  const setCurrentlyEditing = (
    editing: Parameters<typeof setActualCurrentlyEditing>[0],
  ) => {
    if (editing) {
      animateNext();
    }
    setActualCurrentlyEditing(editing);
  };

  const status = useSelector(selectAseStatus);
  // const status = useSelector(selectAseOrganisationId);
  const complianceSubtype = useSelector(selectAseComplianceSubtype);
  const complianceType = useSelector(selectAseComplianceType);
  const template = useSelector(selectAseTemplate);
  const allowedAuthorOrganisations = useSelector(
    selectAseAllowedAuthorOrganisations,
  );
  const allowedAuthors = useSelector(selectAseAllowedAuthors);

  const isOnline = useSelector(selectAseIsOnline);
  const assetId = useSelector(selectAseAssetId);
  const assessmentId = useSelector(selectAseId);
  const assessmentTimestamps = useSelector(selectAseAssetAssessmentTimestamps);
  const tasks = useSelector(selectAseTask);
  const [existingRiskLikelihoodId, existingRiskSeverityId] = useSelector(
    selectAseAssetRiskRatings,
  )[complianceSubtype.compliance_type_id] || ['0', '0'];
  const files = useSelector(selectAseFilesAst);

  const reference = useSelector(selectAseReference);
  const selectedDetails = useSelector(selectAseDetails);

  const dataRef = React.useRef<Details>(cloneDeep(selectedDetails));
  const initDataRef = React.useRef<Details>(cloneDeep(dataRef.current));
  const internalSave = !!isModal;

  if (!internalSave && selectedDetails) {
    dataRef.current = selectedDetails;
    initDataRef.current = dataRef.current;
  }

  const details: Details = internalSave ? dataRef.current! : selectedDetails;

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  isDirtyRef.current = checkIsDirty();

  //

  const currentAssessedTimestamp = dateToNumber(
    new Date(`${details.assessedDate}T${details.assessedTime}`),
  );
  const assessedDateMaxDate = React.useMemo(
    () =>
      numberToDate(
        Math.min(
          ...assessmentTimestamps.filter((ts) => ts > currentAssessedTimestamp),
          dateToNumber(),
        ),
      ),
    [assessmentTimestamps, currentAssessedTimestamp],
  );
  const assessedDateMinDate = React.useMemo(
    () =>
      numberToDate(
        Math.max(
          ...assessmentTimestamps.filter((ts) => ts < currentAssessedTimestamp),
          0,
        ),
      ),
    [assessmentTimestamps, currentAssessedTimestamp],
  );
  const startDateMinDate = React.useMemo(
    () => new Date(details.assessedDate),
    [details.assessedDate],
  );

  const expiryDateMinDate = React.useMemo(
    () => new Date(details.startDate || details.assessedDate),
    [details.assessedDate, details.startDate],
  );

  const authorIds: string[] = details.assessedBy.map((a) => a.id);
  const allowedAuthorsFinal = allowedAuthors.filter(
    (a) => !authorIds.includes(a.id),
  );

  //

  const handleHardSave = React.useCallback(() => {
    if (checkIsDirty()) {
      dispatch(doAseSaveSummary({ data: dataRef.current }));
    } else if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [checkIsDirty, dispatch, isModal, navigation, screenSize]);

  const handleClose = React.useCallback(() => {
    if (isModal) {
      if (checkIsDirty()) {
        confirmUnsaved({
          onSave: handleHardSave,
          onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
        });
        return;
      }
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkIsDirty,
    confirmUnsaved,
    dispatch,
    handleHardSave,
    isModal,
    navigation,
    screenSize,
  ]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<Details>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        if (internalSave) {
          dataRef.current = { ...dataRef.current, ...changes };
          if (
            doForceUpdate === 'always' ||
            (doForceUpdate === 'dirtyChange' &&
              checkIsDirty() !== isDirtyRef.current)
          ) {
            forceUpdate();
          }
        } else {
          dispatch(doAseSaveSummary({ data: newValues }));
        }
      }
    },
    [checkIsDirty, dispatch, forceUpdate, internalSave],
  );

  const saveField = React.useCallback(
    (name: keyof Details, value: string) =>
      handleSoftSave({ [name]: value }, { doForceUpdate: 'dirtyChange' }),
    [handleSoftSave],
  );

  //

  const handleDateChange = React.useCallback(
    (changes: Partial<Details>) => {
      const newValues = { ...dataRef.current, ...changes };
      newValues.startDate = format(
        limitDateToRange(
          newValues.startDateType === 'custom' && newValues.startDate
            ? new Date(newValues.startDate)
            : new Date(newValues.assessedDate),
          {
            min: new Date(newValues.assessedDate),
          },
        ),
        API_DATE_FORMAT,
      );
      newValues.expiryDate = format(
        limitDateToRange(
          newValues.expiryDate ? new Date(newValues.expiryDate) : new Date(),
          {
            min: new Date(newValues.startDate || newValues.assessedDate),
          },
        ),
        API_DATE_FORMAT,
      );
      handleSoftSave(newValues, { doForceUpdate: 'always' });
    },
    [handleSoftSave],
  );

  const handleChangeAssessedDate = React.useCallback(
    (value: string) => handleDateChange({ assessedDate: value }),
    [handleDateChange],
  );

  const handleChangeStartDate = React.useCallback(
    (date: string) =>
      handleDateChange({
        startDate: date,
      }),
    [handleDateChange],
  );

  const handleChangeExpiryDate = React.useCallback(
    (date: string) =>
      handleDateChange({
        expiryDate: date,
      }),
    [handleDateChange],
  );

  const handleChangeExpiryTimescale = (timescale: TimescaleArray) =>
    handleDateChange({
      expiryDateTimescale: timescale,
    });

  const handleChangeStartDateType = (type: typeof details.startDateType) => {
    if (details.startDateType !== type) {
      handleDateChange({
        startDateType: type,
        startDate: details.assessedDate,
      });
    }
    setCurrentlyEditing(undefined);
  };

  const handleSetExpiryDateType = (type: typeof details.expiryDateType) => {
    if (details.expiryDateType !== type) {
      handleDateChange({
        expiryDateType: type,
        expiryDate: format(
          addYears(new Date(details.assessedDate), 1),
          API_DATE_FORMAT,
        ),
        expiryDateTimescale:
          type === 'timescale' ? [1, TimescalePeriod.year] : undefined,
      });
    }
    setCurrentlyEditing(undefined);
  };

  const handleFileChange = React.useCallback(
    (value: string[]) =>
      handleSoftSave(
        {
          fileIds: value,
        },
        { doForceUpdate: 'always' },
      ),
    [handleSoftSave],
  );

  const handleChangeOrganisationPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Performed By Organisation',
          eventKey: eventKeySelectAuthorOrganisationRef.current,
          multiSelect: false,
          searchable: true,
          value: details.authorOrganisation.id
            ? [details.authorOrganisation.id]
            : [],
          options: naturalOrderBy(allowedAuthorOrganisations, 'name').map(
            (allowedAuthor) => ({
              name: allowedAuthor.name,
              internalValue: allowedAuthor.id,
            }),
          ),
        },
      }),
    );

  const eventKeyAttachmentReorderRef = React.useRef(randomKey());

  React.useEffect(() => {
    const events = [
      doAddEvent(eventReorderScreenDone, (action) => {
        if (action.payload.key === eventKeyAttachmentReorderRef.current) {
          handleFileChange(action.payload.stringValues);
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [handleFileChange]);

  const handleStartAttachmentReorder = React.useCallback(() => {
    if (details.fileIds.length < 2) {
      return;
    }
    const options = details.fileIds
      .map((fileId) => {
        const file = files[fileId];
        if (!file) {
          return null;
        }
        return {
          value: fileId,
          name: file.name,
        };
      })
      .filter((item): item is { value: string; name: string } => !!item);

    if (!options.length) {
      return;
    }

    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: 'Reorder Attachments',
          eventKey: eventKeyAttachmentReorderRef.current,
          options,
        },
      }),
    );
  }, [details.fileIds, dispatch, files]);

  const handleAddAuthorsPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Authors',
          eventKey: eventKeySelectAssessorRef.current,
          multiSelect: true,
          searchable: true,
          options: naturalOrderBy(allowedAuthorsFinal, 'name').map(
            (allowedAuthor) => ({
              name: `${allowedAuthor.name} (${allowedAuthor.email})`,
              internalValue: allowedAuthor.id,
            }),
          ),
        },
      }),
    );

  const handleAddAuthors = React.useCallback(
    (addAuthorIds: string[]) => {
      handleSoftSave(
        {
          assessedBy: allowedAuthorsFinal.reduce<
            (typeof details)['assessedBy']
          >(
            (acc, author) => {
              if (addAuthorIds.includes(author.id)) {
                acc.push({
                  id: author.id,
                  name: author.name,
                  email: author.email,
                  role: '',
                });
              }
              return acc;
            },
            [...details.assessedBy],
          ),
        },
        { doForceUpdate: 'always' },
      );
    },
    [allowedAuthorsFinal, details.assessedBy, handleSoftSave],
  );

  const handleRemoveAuthor = React.useCallback(
    (authorId: string) => {
      const newValue = details.assessedBy.filter((a) => a.id !== authorId);
      const removingValue = details.assessedBy.find((a) => a.id === authorId);
      if (newValue.length < 1) {
        alert({
          text: 'Assessments must have at least one author. Add someone else before removing this person',
        });
        return;
      }
      if (!removingValue) {
        return;
      }
      confirm({
        title: 'Remove Author',
        text: `${removingValue.name} may not be able to make changes to this assessment anymore`,
        destructive: true,
        confirmText: 'Remove',
        onConfirm: () =>
          handleSoftSave(
            {
              assessedBy: newValue,
            },
            { doForceUpdate: 'always' },
          ),
      });
    },
    [alert, confirm, details.assessedBy, handleSoftSave],
  );

  const handleModifyAuthorPress = React.useCallback(
    (assessor: Details['assessedBy'][number]) =>
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.AseAuthor,
          params: {
            eventKey: eventKeyAseUpdateAuthor.current,
            data: { ...assessor, role: assessor.role || '' },
          },
        }),
      ),
    [dispatch],
  );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventAseUpdateRiskRating, ({ payload }) => {
        if (payload.key === eventKeyAseUpdateRiskRating.current) {
          handleSoftSave(payload.data, { doForceUpdate: 'always' });
        }
      }),
      doAddEvent(eventAseUpdateAuthor, ({ payload }) => {
        if (payload.key === eventKeyAseUpdateAuthor.current) {
          handleSoftSave(
            {
              assessedBy: details.assessedBy.map((a) =>
                a.id === payload.data.id ? payload.data : a,
              ),
            },
            { doForceUpdate: 'always' },
          );
        }
      }),
      doAddEvent(eventSelectScreenSelection, ({ payload }) => {
        if (payload.key === eventKeySelectAssessorRef.current) {
          handleAddAuthors(payload.stringValues);
        } else if (
          payload.key === eventKeySelectAuthorOrganisationRef.current
        ) {
          const newOrganisationId = payload.stringValues[0];
          const newOrganisation = newOrganisationId
            ? allowedAuthorOrganisations.find((o) => o.id === newOrganisationId)
            : undefined;
          if (newOrganisation) {
            handleSoftSave(
              { authorOrganisation: newOrganisation },
              { doForceUpdate: 'always' },
            );
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [
    allowedAuthorOrganisations,
    details.assessedBy,
    handleAddAuthors,
    handleSoftSave,
  ]);

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(isModal && handleClose);

  // Risk Rating
  const currentRisk:
    | { likelihood: string; rating: string; severity: string }
    | undefined = complianceType.matrix?.outcomes[
    `${existingRiskLikelihoodId}x${existingRiskSeverityId}`
  ]
    ? {
        likelihood:
          complianceType.matrix.likelihoods[existingRiskLikelihoodId]!.name,
        rating:
          complianceType.matrix.ratings[
            complianceType.matrix.outcomes[
              `${existingRiskLikelihoodId}x${existingRiskSeverityId}`
            ]!
          ]!.name,
        severity:
          complianceType.matrix.severities[existingRiskSeverityId]!.name,
      }
    : undefined;
  const {
    riskLikelihoodId: newRiskLikelihoodId,
    riskSeverityId: newRiskSeverityId,
  } = details;
  const changeRisk = newRiskLikelihoodId || newRiskSeverityId;
  const newRisk:
    | { likelihood: string; rating: string; severity: string }
    | undefined = complianceType.matrix?.outcomes[
    `${newRiskLikelihoodId}x${newRiskSeverityId}`
  ]
    ? {
        likelihood:
          complianceType.matrix.likelihoods[newRiskLikelihoodId]!.name,
        severity: complianceType.matrix.severities[newRiskSeverityId]!.name,
        rating:
          complianceType.matrix.ratings[
            complianceType.matrix.outcomes[
              `${newRiskLikelihoodId}x${newRiskSeverityId}`
            ]!
          ]!.name,
      }
    : undefined;

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={`${isModal ? 'Edit ' : ''}Overview`}
          onPressSaveButton={isModal ? handleHardSave : undefined}
          onPressCancelButton={isModal ? handleClose : undefined}
          onPressBackButton={
            !isModal && screenSize === 'small' ? navigation?.goBack : undefined
          }
        />
      }
    >
      {!isModal && (
        <ViewWithSeparators
          separatorMargin={theme.paddingHorizontalHalf / 2}
          separatorColor="transparent"
        >
          <Card contentHorizontal>
            <CardRow
              icon={ICONS.RBPlectrumSolid}
              label={reference ? rbAddReferencePrefix(reference) : undefined}
              style={{ flex: 1 }}
            >
              {!reference && (
                <Txt>
                  {rbAddReferencePrefix('')}
                  <Txt style={{ color: theme.colors.placeholder }}>000000</Txt>
                </Txt>
              )}
            </CardRow>
            <CardRow
              label={status.name}
              icon={ICONS.CircleCheckLight}
              labelColor={`#${status.colour}`}
              iconColor={`#${status.colour}`}
              style={{ flex: 1 }}
            />
          </Card>
          <Card>
            <CardRow
              label="Draft PDF"
              icon={ICONS.FileDuotone}
              labelColor={theme.colors.blue}
              iconColor={theme.colors.blue}
              style={{ flex: 1 }}
              showMenuOnPress
              hideMenuButton
              menuOptions={[
                {
                  text: 'Preview Draft PDF',
                  onPress: () => dispatch(doAseLoadPdf({})),
                },
                {
                  text: str.sharedownload('Draft PDF'),
                  onPress: () => dispatch(doAseLoadPdf({ doShare: true })),
                },
              ]}
            />
          </Card>
        </ViewWithSeparators>
      )}

      {!isModal && !!tasks?.description && (
        <Card label="Notes for the Assessor">
          <CardRow label={tasks.description} labelNumberOfLines={0} />
        </Card>
      )}

      <Card>
        <TextField
          textLeft="Custom Reference"
          textLeftStyle={{ color: theme.colors.black }}
          placeholder="None"
          value={details.customReference}
          onChangeWithName={saveField}
          inputStyle={{ textAlign: 'right' }}
          name="customReference"
          textIcon={ICONS.HashtagLight}
          showTextIcon
        />
        <DateField
          label="Assessed Date"
          value={details.assessedDate}
          onChange={handleChangeAssessedDate}
          min={assessedDateMinDate}
          max={assessedDateMaxDate}
          clearable={false}
          showFieldTypeIcon={false}
        />

        {currentlyEditing === 'startdate' ? (
          <Card expandedInline marginHorizontal={false}>
            <CardRow
              label="Valid from Assessed Date"
              radioValue={details.startDateType === 'assessed'}
              onPress={() => handleChangeStartDateType('assessed')}
            />
            <CardRow
              label="Custom Valid From Date"
              radioValue={details.startDateType === 'custom'}
              onPress={() => handleChangeStartDateType('custom')}
            />
          </Card>
        ) : details.startDateType === 'custom' && details.startDate ? (
          <ViewWithSeparators
            horizontal
            style={{
              backgroundColor: theme.colors.white,
            }}
          >
            <CardRow
              label="Valid From"
              value="Date"
              onPress={() => setCurrentlyEditing('startdate')}
              style={{ flex: 1 }}
            />
            <DateField
              value={details.startDate}
              onChange={handleChangeStartDate}
              min={startDateMinDate}
              clearable={false}
              showFieldTypeIcon={false}
              shrinkToWidth
            />
          </ViewWithSeparators>
        ) : (
          <CardRow
            label="Valid From"
            value="Assessed Date"
            onPress={() => setCurrentlyEditing('startdate')}
          />
        )}

        {currentlyEditing === 'expirydate' ? (
          <Card expandedInline expandedInlineLast marginHorizontal={false}>
            <CardRow
              label="Valid From + Asset's Validity Timescale"
              radioValue={details.expiryDateType === 'asset'}
              onPress={() => handleSetExpiryDateType('asset')}
            />
            <CardRow
              label="Valid From + Assessment's Validity Timescale"
              radioValue={details.expiryDateType === 'assessment_template'}
              onPress={() => handleSetExpiryDateType('assessment_template')}
            />
            <CardRow
              label="Valid From + Custom Timescale"
              radioValue={details.expiryDateType === 'timescale'}
              onPress={() => handleSetExpiryDateType('timescale')}
            />
            <CardRow
              label="Custom Date"
              radioValue={details.expiryDateType === 'custom'}
              onPress={() => handleSetExpiryDateType('custom')}
            />
          </Card>
        ) : details.expiryDateType === 'custom' && details.expiryDate ? (
          <ViewWithSeparators
            horizontal
            style={{
              backgroundColor: theme.colors.white,
            }}
          >
            <CardRow
              label="Valid Until"
              value="Date"
              onPress={() => setCurrentlyEditing('expirydate')}
              style={{ flex: 1 }}
            />
            <DateField
              value={details.expiryDate}
              onChange={handleChangeExpiryDate}
              min={expiryDateMinDate}
              clearable={false}
              showFieldTypeIcon={false}
              shrinkToWidth
            />
          </ViewWithSeparators>
        ) : details.expiryDateType === 'timescale' &&
          !!details.expiryDateTimescale ? (
          <ViewWithSeparators
            horizontal
            style={{
              backgroundColor: theme.colors.white,
            }}
          >
            <CardRow
              label="Valid Until"
              value="Timescale"
              onPress={() => setCurrentlyEditing('expirydate')}
              style={{ flex: 1 }}
            />
            <CardRow
              style={{
                backgroundColor: 'white',
                width: 144,
              }}
              contentStyle={{
                paddingVertical: 0,
              }}
            >
              <TimescaleField
                timescale={details.expiryDateTimescale}
                onChange={handleChangeExpiryTimescale}
              />
            </CardRow>
          </ViewWithSeparators>
        ) : details.expiryDateType === 'assessment_template' ? (
          <CardRow
            label="Valid Until"
            value="Assessment's Validity"
            onPress={() => setCurrentlyEditing('expirydate')}
          />
        ) : (
          <CardRow
            label="Valid Until"
            value="Asset's Validity"
            onPress={() => setCurrentlyEditing('expirydate')}
          />
        )}
      </Card>

      {!!details.authorOrganisation.id && (
        <Card label="Performed By">
          <CardRow
            label={details.authorOrganisation.name}
            onPress={
              allowedAuthorOrganisations.length > 1
                ? handleChangeOrganisationPress
                : undefined
            }
          />
        </Card>
      )}

      <Card
        label="Authors"
        labelLinkText="Add Author"
        onLabelLinkPress={
          allowedAuthorsFinal.length ? handleAddAuthorsPress : undefined
        }
      >
        {naturalOrderBy(details.assessedBy, (v) => `${v.name} ${v.email}`).map(
          (assessor) => {
            return (
              <CardRow
                key={assessor.email}
                label={`${assessor.name} (${assessor.email})`}
                value={assessor.role || 'Assessor'}
                labelAlignTop
                onPress={() => handleModifyAuthorPress(assessor)}
                menuOptions={[
                  {
                    text: 'Change Role',
                    onPress: () => handleModifyAuthorPress(assessor),
                  },
                  {
                    text: 'Remove Author',
                    destructive: true,
                    icon: ICONS.TrashCanLight,
                    onPress: () => handleRemoveAuthor(assessor.id),
                  },
                ]}
              />
            );
          },
        )}
      </Card>

      {complianceType.matrix && (
        <Card label="Risk Rating">
          <CardRow
            label="Current Risk Rating"
            labelAlignTop={!!currentRisk}
            value={
              currentRisk
                ? `${currentRisk.likelihood} x ${currentRisk.severity} = ${currentRisk.rating}`
                : 'No Rating'
            }
          />
          <CardRow
            label="New Risk Rating"
            labelAlignTop={!!changeRisk}
            value={
              changeRisk
                ? (newRisk
                    ? `${newRisk.likelihood} x ${newRisk.severity} = ${newRisk.rating}`
                    : 'No Rating') +
                  (details.riskChangeReason
                    ? '\nReason Changed'
                    : '\nReason Not Changed')
                : 'Not Changed'
            }
            navigationType="modal"
            onPress={() =>
              dispatch(
                NAVIGATE_MODAL({
                  screen: ModalScreens.ASERISKRATING,
                  params: {
                    eventKey: eventKeyAseUpdateRiskRating.current,
                    data: {
                      riskLikelihoodId: dataRef.current.riskLikelihoodId,
                      riskSeverityId: dataRef.current.riskSeverityId,
                      riskChangeReason: dataRef.current.riskChangeReason,
                      riskReason: dataRef.current.riskReason,
                    },
                  },
                }),
              )
            }
          />
        </Card>
      )}

      <Card label="Assessor Remarks">
        <TextAreaField
          placeholder="No Remarks"
          value={details.remarksAssessor}
          onChangeWithName={saveField}
          name="remarksAssessor"
        />
      </Card>

      <Card label="Agreed Scope">
        <TextAreaField
          placeholder={template.default_remarks_scope}
          value={details.remarksScope}
          onChangeWithName={saveField}
          name="remarksScope"
        />
      </Card>

      <Card label="Changes To Scope">
        <TextAreaField
          placeholder={template.default_remarks_scope_change}
          value={details.remarksScopeChange}
          onChangeWithName={saveField}
          name="remarksScopeChange"
        />
      </Card>

      <Card
        label="Attachments"
        labelLinkText={details.fileIds.length > 1 ? 'Reorder' : undefined}
        onLabelLinkPress={
          details.fileIds.length > 1 ? handleStartAttachmentReorder : undefined
        }
      >
        <FileField
          context={{
            context: isOnline
              ? FilesContextEnum.remoteAssessment
              : FilesContextEnum.localAssessment,
            assetId,
            assessmentId,
          }}
          value={details.fileIds}
          onChange={handleFileChange}
          promptMessage="Add to Assessment"
          multiSelect
        />
      </Card>

      {!isModal && (
        <Card label="Assessment Template">
          <CardRow label="Name" value={template.name} />
          <CardRow
            label="Title"
            value={template.title || complianceSubtype.name}
          />
        </Card>
      )}

      {!isOnline && (
        <Button
          text="Delete Assessment"
          onPress={() => dispatch(DELETE_ASSESSMENT())}
          destructive
          style={{ alignSelf: 'center' }}
        />
      )}
    </Page>
  );
};
